import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * Badge atoms — codified from Figma "Base Badge" spec.
 *
 * Surface-contrast rule: a badge's fill is the INVERSE of the surface it
 * sits on, so it always reads.
 *   • Badge on `--secondary`                  → `muted`    (`bg-card`)
 *   • Badge on anything else (card/page/input)→ `primary`  (`bg-secondary`)
 *   • High-emphasis pill (inverted)            → `inverse` (`bg-foreground`)
 *
 * Multi-select chips inside AutocompleteArrayInput pick `primary`/`muted`
 * automatically based on the input's own variant.
 *
 * All variants are 24px tall, 6px radius, 4×8 padding, with 1px border-border
 * and backdrop-blur for the glass effect — per the foundational Atom spec.
 */
const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,background-color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        completed:
          'capitalize px-2 py-1.5 rounded-sm bg-secondary text-foreground border-border',
        // Spec: bg amber@20%, border solid amber (#FF9500), foreground text
        incomplete:
          'capitalize px-2 py-1.5 rounded-sm bg-amber/20 border-amber text-foreground',
        ['in-review']:
          'capitalize px-4 py-1 border-1 border-indigo-500 bg-review',
        matchmaking:
          'capitalize px-4 py-1 border-1 bg-transparent text-foreground',
        /* ─── Spec atoms (Figma "Base Badge") ─────────────────────────── */
        /** Default badge sitting on `--card` / page panel. */
        primary:
          'h-6 px-2 py-1 gap-1 rounded-md bg-secondary border-border text-foreground backdrop-blur-2xl',
        /** Badge sitting on a `--secondary` surface — one step recessed.
         *  Text stays `foreground` for legibility (value/multi-select chips);
         *  use the dedicated grey-text treatment only for de-emphasized meta. */
        muted:
          'h-6 px-2 py-1 gap-1 rounded-md bg-card border-border text-foreground backdrop-blur-2xl',
        /** High-emphasis solid pill (inverted bg/text), no border. */
        inverse:
          'h-6 px-2 py-1 gap-1 rounded-md bg-foreground border-transparent text-background',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
